import { Link } from "react-router-dom";
import { Clock, Tag } from "lucide-react";

const PUBLISH_LABEL = {
  it: { published: "Articolo", scheduled: "Prossimamente" },
  en: { published: "Article", scheduled: "Coming soon" },
};

const MONTH_IT = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

function formatDate(iso, lang = "it") {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  if (lang === "en") {
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "long" });
  }
  return `${MONTH_IT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Editorial blog card — used both on `/blog` index and the homepage
 * "Ti può interessare" section.
 *
 * Scheduled posts render as a non-clickable teaser with a "Prossimamente"
 * eyebrow so the editorial calendar is visible without exposing 404 traps.
 */
const BlogCard = ({ post, lang = "it", compact = false }) => {
  const isScheduled = post.status === "scheduled";
  const eyebrow = isScheduled
    ? `${PUBLISH_LABEL[lang].scheduled} · ${formatDate(post.publish_date, lang)}`
    : `${PUBLISH_LABEL[lang].published} · ${formatDate(post.publish_date, lang)}`;

  const Inner = (
    <>
      {post.cover_image && (
        <div
          className="w-full aspect-[16/9] bg-cover bg-center border-b-2 border-[#282C2A]"
          style={{
            backgroundImage: `url(${post.cover_image})`,
            filter: isScheduled ? "grayscale(80%) brightness(0.9)" : "none",
          }}
        />
      )}
      <div className={compact ? "p-4" : "p-5"}>
        <div
          className={`label-eyebrow ${
            isScheduled ? "text-[#A89968]" : "text-[#4A5D23]"
          }`}
        >
          {eyebrow}
        </div>
        <h3
          className={`font-serif-display text-[#282C2A] mt-2 leading-tight ${
            compact ? "text-xl" : "text-2xl"
          }`}
        >
          {post.title}
        </h3>
        {!compact && (
          <p className="mt-2 text-sm text-[#6C706D] leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}
        <div className="mt-4 flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[#6C706D]">
          {post.read_time_min && (
            <span className="inline-flex items-center gap-1">
              <Clock size={11} strokeWidth={1.5} />
              {post.read_time_min} min
            </span>
          )}
          {(post.tags || []).slice(0, 2).map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1">
              <Tag size={11} strokeWidth={1.5} />
              {tag}
            </span>
          ))}
        </div>
      </div>
    </>
  );

  if (isScheduled) {
    return (
      <article
        data-testid={`blog-card-scheduled-${post.slug}`}
        className="bg-white border-2 border-[#E6E4DD] shadow-[0_2px_0_#E6E4DD] opacity-90 cursor-default"
      >
        {Inner}
      </article>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="block bg-white border-2 border-[#282C2A] shadow-paper hover:shadow-[0_4px_0_#282C2A] hover:-translate-y-0.5 transition-all"
    >
      {Inner}
    </Link>
  );
};

export default BlogCard;
